import { z } from "zod";

export const OutsourceApInstallmentPlanTemplateRowSchema = z.object({
  installmentNo: z.number().int().positive(),
  label: z.string().max(200).nullable().optional(),
  labelEn: z.string().max(200).nullable().optional(),
  percent: z.number().int().min(1).max(100),
  dueOffsetDays: z.number().int().min(0),
});
export type OutsourceApInstallmentPlanTemplateRowDTO = z.infer<
  typeof OutsourceApInstallmentPlanTemplateRowSchema
>;

export const OutsourceApInstallmentPlanTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  isDefault: z.boolean(),
  status: z.enum(["active", "inactive"]),
  sortOrder: z.number(),
  createdBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  rows: z.array(OutsourceApInstallmentPlanTemplateRowSchema),
});
export type OutsourceApInstallmentPlanTemplateDTO = z.infer<
  typeof OutsourceApInstallmentPlanTemplateSchema
>;

function refineInstallmentRows(
  rows: OutsourceApInstallmentPlanTemplateRowDTO[],
  ctx: z.RefinementCtx,
  pathPrefix: (string | number)[] = ["rows"],
) {
  const sum = rows.reduce((acc, row) => acc + row.percent, 0);
  if (sum !== 100) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "ຜົນລວມ % ຕ້ອງເທົ່າ 100",
      path: pathPrefix,
    });
  }

  const nos = rows.map((row) => row.installmentNo).sort((a, b) => a - b);
  const unique = new Set(nos);
  if (unique.size !== nos.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "ເລກງວດຕ້ອງບໍ່ຊ້ຳກັນ",
      path: pathPrefix,
    });
  }

  for (let i = 0; i < nos.length; i++) {
    if (nos[i] !== i + 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ເລກງວດຕ້ອງເປັນ 1…N ຕິດກັນ",
        path: pathPrefix,
      });
      break;
    }
  }
}

export const CreateOutsourceApInstallmentPlanTemplateSchema = z
  .object({
    name: z.string().min(1, "ຕ້ອງປ້ອນຊື່ແມ່ແບບ"),
    isDefault: z.boolean().optional(),
    status: z.enum(["active", "inactive"]).optional(),
    sortOrder: z.number().int().optional(),
    rows: z.array(OutsourceApInstallmentPlanTemplateRowSchema).min(1),
  })
  .superRefine((value, ctx) => {
    refineInstallmentRows(value.rows, ctx);
  });
export type CreateOutsourceApInstallmentPlanTemplateDTO = z.infer<
  typeof CreateOutsourceApInstallmentPlanTemplateSchema
>;

export const UpdateOutsourceApInstallmentPlanTemplateSchema = z
  .object({
    name: z.string().min(1).optional(),
    isDefault: z.boolean().optional(),
    status: z.enum(["active", "inactive"]).optional(),
    sortOrder: z.number().int().optional(),
    rows: z.array(OutsourceApInstallmentPlanTemplateRowSchema).min(1).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: "ຕ້ອງມີຢ່າງນ້ອຍ 1 ຟິວທີ່ແກ້ໄຂ",
  })
  .superRefine((value, ctx) => {
    if (value.rows) refineInstallmentRows(value.rows, ctx);
  });
export type UpdateOutsourceApInstallmentPlanTemplateDTO = z.infer<
  typeof UpdateOutsourceApInstallmentPlanTemplateSchema
>;
